import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isEmpty } from 'lodash/fp';

import {
    Alert,
    FieldHintText,
    FieldLabel,
    FlexColumn,
    FlexRow,
    Loading,
    Modal,
    ModalBody,
    ModalButton,
    ModalFooter,
    ModalHeader,
    RadioSelect,
} from '@portal/common/components';
import { AGE_GROUP_ID_KEY, MEASURE_ID_KEY, METRIC_ID_KEY } from '@portal/common/models/data-key';
import { styled } from '@portal/common/theme';
import { DataFilters, DataType } from '@portal/common/types';
import { localizeFilterValues } from '@portal/common/utility/chart-data-helpers';
import { formatValue } from '@portal/common/utility/filters-helpers';
import renderErrors from '@portal/common/utility/renderErrors';

import _ from '../../../locale';
import { getConditionDetailNotes } from '../../../store/data-explorer/actions';
import {
    getAgeGroups,
    getConditionDetailNotes as getConditionDetailNotesSelector,
    getSelectedConditions,
    getSingleYearAgeGranularity,
} from '../../../store/data-explorer/selectors';
import { getSelectedDataCollectionForDataTool } from '../../../store/user-settings/selectors';
import { SingleAgeReportTypes } from '../../../types';
import { pluralizeVerb } from '../../../utility/string-utils';
import CustomAgeGroupSelector from './CustomAgeGroupSelector';
import SavedAgeGroups, { AGE_GROUP_SEPARATOR } from './SavedAgeGroups';
import useConditionsHaveNotModeledAgeGroups from './use-conditions-have-not-modeled-age-groups';
import { useRaceEthnicityAgeOptionsRestriction } from './use-race-ethnicity-age-options-restriction';
import useReportData from './use-report-data';
import useValidation from './use-validation';

const Hint = styled(FieldHintText)({
    marginBottom: 16,
});

const Divider = styled.div({
    height: 30,
});

const StyledModal = styled(Modal)({
    width: 550,
});

export const DATE_FORMAT = 'mm/dd/yyyy';

export type APIError = {
    code?: number;
    message: string;
    explanation?: string;
    fields?: Record<string, string>;
};

export const ERROR_MESSAGES = {
    CANNOT_CALCULATE_SINGLE_YEAR: 'Can not calculate Single Year Ages for all selected filters',
    CANNOT_CALCULATE_CUSTOM_GROUP: 'Can not calculate Custom Age Group for all selected filters',
    MISSING_AGE_GROUP_DATA: 'Missing Age Group Data',
} as const;

export type AgeRange = [number | string, number | string];

type Props = {
    isVisible: boolean;
    setVisibility: (isVisible: boolean) => void;
    supportedAgeRange: {
        min: number;
        max: number;
    };
    supportedDataTypes: DataType[];
    selectedFilters: DataFilters;
    selectedConditionsDataTypes: DataType[] | null;
    dataProjectId?: number;
    selectedMeasures?: number[];
};

/**
 * Parses filter suggestion errors and returns a formatted React element
 */
const parseFilterSuggestionError = (message: string, explanation?: string): React.ReactNode => {
    try {
        const match = explanation?.match(/\{.*\}/);
        if (!match) return message;

        const suggestionToRemove = JSON.parse(match[0]);
        return (
            <FlexColumn itemsSpacing={10}>
                <div>{message}</div>
                <div>Try removing at least one of the following filters:</div>
                <ul>
                    {Object.entries(suggestionToRemove).map(([key, value]) => (
                        <li key={key}>
                            {`• ${_(`refinement_${key}`)}: ${formatValue(key, value, _)}`}
                        </li>
                    ))}
                </ul>
            </FlexColumn>
        );
    } catch (e) {
        console.error('Failed to parse error explanation:', e);
        return message;
    }
};

/**
 * Parses missing age group errors and returns a formatted React element
 */
const parseMissingAgeGroupError = (message: string, explanation?: string): React.ReactNode => {
    if (!explanation) return message;

    try {
        const ageGroupPattern = /the following age groups are missing: ([\d,]+)/;
        const match = explanation.match(ageGroupPattern);

        if (!match) return explanation;

        const ageGroupsIds = match[1].split(',').map(Number);
        const ageGroups = localizeFilterValues(AGE_GROUP_ID_KEY, ageGroupsIds);
        const formattedExplanation = explanation.replace(match[1], ageGroups);

        return (
            <FlexColumn itemsSpacing={10}>
                <div style={{ textAlign: 'center' }}>{message}</div>
                <div>{formattedExplanation}</div>
            </FlexColumn>
        );
    } catch (e) {
        console.error('Failed to parse age group error explanation:', e);
        return explanation;
    }
};

/**
 * Parses API errors and returns appropriate React elements for display
 */
const parseError = (error: APIError): React.ReactNode => {
    const { fields, code, message, explanation } = error;

    // If we have field-specific errors, render those
    if (fields) {
        return renderErrors(fields);
    }

    // Handle specific error types for 400 errors
    if (code === 400) {
        switch (message) {
            case ERROR_MESSAGES.CANNOT_CALCULATE_SINGLE_YEAR:
            case ERROR_MESSAGES.CANNOT_CALCULATE_CUSTOM_GROUP:
                return parseFilterSuggestionError(message, explanation);

            case ERROR_MESSAGES.MISSING_AGE_GROUP_DATA:
                return parseMissingAgeGroupError(message, explanation);

            default:
                // For other 400 errors, just return the message
                return message;
        }
    }

    // Default case: return the error message
    return message;
};

const ExportSingleAgeEstimatesModal: React.FC<Props> = ({
    isVisible,
    setVisibility,
    supportedAgeRange,
    selectedFilters,
    selectedConditionsDataTypes,
    supportedDataTypes,
    dataProjectId,
    selectedMeasures = [],
}) => {
    const dispatch = useDispatch();
    const selectedDataCollection = useSelector(getSelectedDataCollectionForDataTool);
    const granularity = useSelector(getSingleYearAgeGranularity);
    const conditionDetailNotes = useSelector(getConditionDetailNotesSelector);
    const selectedConditions = useSelector(getSelectedConditions);
    const ageGroupEntities = useSelector(getAgeGroups);
    const DEFAULT_AGE_RANGE: AgeRange = [supportedAgeRange.min, supportedAgeRange.max];
    const [currentAgeGroup, setCurrentAgeGroup] = useState<AgeRange>(DEFAULT_AGE_RANGE);

    const [savedAgeGroups, setSavedAgeGroups] = useState<string[]>([]);
    const [isSavedAgeGroupsControlVisible, setSavedAgeRangesControlVisible] = useState(false);
    const [isAgeRangeValid, setIsAgeRangeValid] = useState(true);

    const [selectedReportType, setSelectedReportType] = useState<SingleAgeReportTypes>(
        SingleAgeReportTypes.Normal
    );

    const supportedMetrics = (granularity?.[METRIC_ID_KEY] as number[]) || [];
    const selectedMetrics = useMemo(
        () => (selectedFilters?.[METRIC_ID_KEY] as number[]) || [],
        [selectedFilters]
    );

    const selectedDataTypes = useMemo(
        () => selectedConditionsDataTypes || [],
        [selectedConditionsDataTypes]
    );

    const unsupportedDataTypes = useMemo(
        () => selectedDataTypes.filter((type) => !supportedDataTypes.includes(type)),
        [selectedDataTypes, supportedDataTypes]
    );

    const selectedSupportedDataTypes = useMemo(
        () => selectedDataTypes.filter((type) => supportedDataTypes.includes(type)),
        [selectedDataTypes, supportedDataTypes]
    );

    const unsupportedMetrics = useMemo(
        () => selectedMetrics.filter((id) => !supportedMetrics.includes(id)),
        [selectedMetrics, supportedMetrics]
    );

    const selectedSupportedMetrics = useMemo(
        () => selectedMetrics.filter((id) => supportedMetrics.includes(id)),
        [selectedMetrics, supportedMetrics]
    );

    const isRestrictedDataTypeSelected = !isEmpty(unsupportedDataTypes);
    const isRestrictedMetricSelected = granularity?.[METRIC_ID_KEY] && !isEmpty(unsupportedMetrics);

    const isSingleAgesReport = selectedReportType === SingleAgeReportTypes.Normal;

    const { hasRestrictedAgeRanges, restrictedAgeRangesMessage } =
        useRaceEthnicityAgeOptionsRestriction({
            dataProjectId,
            selectedMeasures,
            isSingleAgesReport,
        });

    const showRestrictedAgeSubmitHint = useMemo(
        () =>
            hasRestrictedAgeRanges &&
            !isAgeRangeValid &&
            (isSingleAgesReport || isEmpty(savedAgeGroups)),
        [hasRestrictedAgeRanges, isAgeRangeValid, isSingleAgesReport, savedAgeGroups]
    );

    const { inProgress, errors, fetchReport, setErrors } = useReportData(
        selectedFilters,
        currentAgeGroup,
        selectedDataTypes,
        supportedDataTypes,
        selectedDataCollection,
        granularity,
        isSingleAgesReport,
        savedAgeGroups,
        selectedMetrics,
        supportedMetrics,
        supportedAgeRange
    );

    const { isCardinalityLimitExceeded, isValid } = useValidation(
        currentAgeGroup,
        savedAgeGroups,
        selectedFilters,
        granularity,
        isSingleAgesReport
    );

    const conditionsHaveNotModeledAgeGroups = useConditionsHaveNotModeledAgeGroups({
        conditionDetailNotes,
        selectedFilters,
        currentAgeGroup,
        savedAgeGroups,
        isSingleAgesReport,
        ageGroupEntities,
    });

    const handleCloseModal = useCallback(() => {
        reset();
        setVisibility(false);
    }, [setVisibility]);

    const reset = useCallback(() => {
        setCurrentAgeGroup(DEFAULT_AGE_RANGE);
        setSavedAgeGroups([]);
        setSavedAgeRangesControlVisible(false);
        setSelectedReportType(SingleAgeReportTypes.Normal);
        setErrors(null);
        setIsAgeRangeValid(true);
    }, []);

    const handleAgeRangeValidityChange = useCallback((valid: boolean) => {
        setIsAgeRangeValid(valid);
    }, []);

    const handleReportTypeChange = useCallback(({ target: { value } }) => {
        setSelectedReportType(value);
    }, []);

    const updateSelectedAgeGroups = useCallback(({ target: { value } }) => {
        setSavedAgeGroups(value);
    }, []);

    const handleAddAggregatedRange = useCallback(
        (range: number[]) => {
            const [from, to] = range;
            const newAgeGroup = [from, AGE_GROUP_SEPARATOR, to].join('');
            if (!savedAgeGroups.includes(newAgeGroup)) {
                setSavedAgeGroups([...savedAgeGroups, newAgeGroup]);
            }
            setSavedAgeRangesControlVisible(true);
        },
        [savedAgeGroups]
    );

    const handleAgeGroupChange = useCallback((range: number[]) => {
        setCurrentAgeGroup([range[0], range[1]] as AgeRange);
    }, []);

    useEffect(() => {
        if (isVisible) {
            dispatch(
                getConditionDetailNotes.request(selectedConditions, {
                    measure_id: selectedFilters?.[MEASURE_ID_KEY] as number[],
                })
            );
        }
        reset();
        setErrors(isCardinalityLimitExceeded ? { message: _('error_limit_exceeded') } : null);
    }, [isVisible]);

    useEffect(() => {
        setErrors(isCardinalityLimitExceeded ? { message: _('error_limit_exceeded') } : null);
    }, [isCardinalityLimitExceeded]);

    useEffect(() => {
        setCurrentAgeGroup(DEFAULT_AGE_RANGE);
    }, [isSingleAgesReport]);

    const handleSubmit = async (ev: FormEvent) => {
        ev?.preventDefault();

        const success = await fetchReport();
        if (success) {
            setVisibility(false);
        }
    };

    const isSubmitDisabled = useMemo(
        () =>
            !isValid ||
            (isSingleAgesReport && !isAgeRangeValid) ||
            (!isSingleAgesReport && isEmpty(savedAgeGroups) && !isAgeRangeValid),
        [isValid, isSingleAgesReport, isAgeRangeValid, savedAgeGroups]
    );

    return (
        <StyledModal show={isVisible} onHide={handleCloseModal}>
            {errors && <Alert color="error">{parseError(errors)}</Alert>}
            <form onSubmit={handleSubmit}>
                <ModalHeader>{_('export_single_age_modal_title')}</ModalHeader>
                <ModalBody>
                    {inProgress ? (
                        <FlexColumn justify="center">
                            <FlexRow justify="center">
                                <span>{_('export_single_age_modal_loading_text')}</span>
                            </FlexRow>
                            <Loading />
                        </FlexColumn>
                    ) : (
                        <FlexColumn itemsSpacing={12}>
                            {isRestrictedDataTypeSelected && (
                                <Hint>
                                    {_('export_single_age_modal_hint_text', {
                                        unsupported: unsupportedDataTypes
                                            .map((dataType) => _(`data_type_${dataType}`))
                                            .join(', '),
                                        supported: selectedSupportedDataTypes
                                            .map((dataType) => _(`data_type_${dataType}`))
                                            .join(', '),
                                        verb: pluralizeVerb(unsupportedDataTypes.length),
                                    })}
                                </Hint>
                            )}
                            {isRestrictedMetricSelected && (
                                <Hint>
                                    {_('export_single_age_modal_hint_text', {
                                        unsupported: unsupportedMetrics
                                            .map((id) => _(`metric_${id}`))
                                            .join(', '),
                                        supported: selectedSupportedMetrics
                                            .map((id) => _(`metric_${id}`))
                                            .join(', '),
                                        verb: pluralizeVerb(unsupportedMetrics.length),
                                    })}
                                </Hint>
                            )}
                            {!isEmpty(conditionsHaveNotModeledAgeGroups) && (
                                <Hint>
                                    {_(
                                        'export_single_age_modal_warning_conditions_have_not_modeled_age_groups',
                                        {
                                            conditions:
                                                conditionsHaveNotModeledAgeGroups.join(', '),
                                        }
                                    )}
                                </Hint>
                            )}
                            <FieldLabel>{_('export_single_report_type_selector_label')}</FieldLabel>
                            <RadioSelect
                                name="report_type_selector"
                                value={selectedReportType}
                                options={Object.values(SingleAgeReportTypes).map((type) => ({
                                    value: type,
                                    label: _(`export_single_report_type_${type}`),
                                }))}
                                onChange={handleReportTypeChange}
                            />
                            {showRestrictedAgeSubmitHint ? (
                                <FieldHintText>{restrictedAgeRangesMessage}</FieldHintText>
                            ) : (
                                <Divider />
                            )}
                            <CustomAgeGroupSelector
                                supportedAgeRange={supportedAgeRange}
                                isSingleRangeReport={isSingleAgesReport}
                                onChange={handleAgeGroupChange}
                                onSave={handleAddAggregatedRange}
                                selectedMetrics={selectedMetrics}
                                selectedFilters={selectedFilters}
                                dataProjectId={dataProjectId}
                                onValidityChange={handleAgeRangeValidityChange}
                            />
                            {isSavedAgeGroupsControlVisible && !isSingleAgesReport && (
                                <SavedAgeGroups
                                    value={savedAgeGroups}
                                    onChange={updateSelectedAgeGroups}
                                />
                            )}
                        </FlexColumn>
                    )}
                </ModalBody>
                {!inProgress && (
                    <ModalFooter>
                        <ModalButton color="secondary" onClick={handleCloseModal}>
                            {_('cancel_button')}
                        </ModalButton>
                        <ModalButton type="submit" disabled={isSubmitDisabled}>
                            {_('export_single_age_modal_submit_label')}
                        </ModalButton>
                    </ModalFooter>
                )}
            </form>
        </StyledModal>
    );
};

export default ExportSingleAgeEstimatesModal;
